/**
 * UTT's frontend trades futures, through Tradovate, and nothing else.
 *
 * tradovate-service can host brokers on other platforms (Vest perps, which are
 * driven from the phone overlay, not from here) and still reports them in
 * `broker:list` and on every per-broker socket event. This is the gate that
 * keeps them out: a broker whose `platform` is anything other than "tradovate"
 * (a missing platform means tradovate) is never stored, never made active,
 * never toasted about, and so can never be the target of an order.
 *
 * `broker:list` is the entry point that learns which ids are hidden; every
 * per-broker store action then drops events for those ids.
 */

const FUTURES_PLATFORM = 'tradovate';

const hiddenBrokerIds = new Set<string>();

export function isFuturesBroker(broker: unknown): boolean {
  const platform = (broker as { platform?: unknown } | null | undefined)?.platform;
  return platform === undefined || platform === null || platform === FUTURES_PLATFORM;
}

/**
 * Keep only futures brokers, and remember the ids of the rest so their
 * per-broker events can be dropped too.
 */
export function filterFuturesBrokers<T extends { id: string; platform?: unknown }>(brokers: T[]): T[] {
  const kept: T[] = [];
  for (const broker of brokers) {
    if (isFuturesBroker(broker)) {
      hiddenBrokerIds.delete(broker.id);
      kept.push(broker);
    } else {
      hiddenBrokerIds.add(broker.id);
    }
  }
  return kept;
}

export function isHiddenBrokerId(brokerId: string | null | undefined): boolean {
  return !!brokerId && hiddenBrokerIds.has(brokerId);
}

/** For tests. */
export function _resetHiddenBrokers(): void {
  hiddenBrokerIds.clear();
}
